import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useLocale } from './LocaleProvider'
import { isSupportedCode } from './resolveLocale'

/**
 * Applies `?lang=` when the query string changes.
 *
 * The provider resolves the language once, at mount, which covers somebody opening a shared
 * link. It does not cover a link followed inside the app that carries a different `?lang=`,
 * and that case is real: the footer and the sitemap both link with the parameter attached.
 *
 * The effect deliberately depends on the search string alone. Depending on the current locale
 * as well produces a loop that silently undoes the language switcher: the switcher sets the
 * locale, the locale change re-runs this effect, and the effect reads the *old* `?lang=` still
 * in the URL and sets it straight back. The ref makes the guard explicit rather than relying
 * on a dependency array to express it.
 *
 * Lives inside the router because it needs the location; the provider itself sits above the
 * router so the theme direction is settled before anything renders.
 */
export function LocaleUrlSync() {
  const { setLocale } = useLocale()
  const { search } = useLocation()
  const lastApplied = useRef<string | null>(null)

  useEffect(() => {
    const requested = new URLSearchParams(search).get('lang')
    if (!requested || requested === lastApplied.current) return
    if (isSupportedCode(requested)) {
      lastApplied.current = requested
      setLocale(requested)
    }
  }, [search, setLocale])

  return null
}
